"use client";

import { useState } from "react";

type ShareBarProps = {
  title: string;
  summary?: string;
  url: string; // URL assoluto canonico
  context?: string; // "event-edition" | "event-mother" | "listing" | ...
  placement?: "top" | "bottom";
  align?: "left" | "center" | "right";
  compact?: boolean;
  comune?: string; // Comune della sagra
  provinciaSigla?: string; // Sigla della provincia (es. "SO")
  dateRange?: string; // Date formattate (es. "15/16 novembre")
};

function withUTM(baseUrl: string) {
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}utm_source=share`;
}

function trackShare(eventName: string, payload: Record<string, any>) {
  try {
    const w = window as any;
    if (w.posthog?.capture) w.posthog.capture(eventName, payload);
    if (w.amplitude?.getInstance) w.amplitude.getInstance().logEvent(eventName, payload);
  } catch {}
}

export function ShareBar({
  title,
  summary,
  url,
  context = "unknown",
  placement = "top",
  align = "left",
  compact = false,
  comune,
  provinciaSigla,
  dateRange,
}: ShareBarProps) {
  const [copied, setCopied] = useState(false);

  const abs = url;

  const links = {
    native: withUTM(abs),
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withUTM(abs))}`,
    copy: withUTM(abs),
  };

  // Costruisce il nuovo formato del messaggio
  let text: string;
  if (comune && (provinciaSigla || dateRange)) {
    // Nuovo formato: "Ho trovato questa sagra a [COMUNE] ([PROVINCIA])!\n\n[Nome sagra] [date].\n\nGuarda qui 👉 [URL]"
    const comunePart = provinciaSigla ? `${comune} (${provinciaSigla})` : comune;
    // Estrae il nome della sagra dal title rimuovendo " a [comune]" (mantiene l'anno se presente)
    const sagraName = title.replace(/\s+a\s+.*$/, "").trim();
    const datePart = dateRange || "";
    const sagraLine = datePart ? `${sagraName} ${datePart}.` : `${sagraName}.`;

    text = `Ho trovato questa sagra a ${comunePart}!\n\n${sagraLine}\n\nGuarda qui 👉 ${links.native}`;
  } else {
    // Fallback al formato vecchio se non ci sono abbastanza dati
    const inviteLine = "Invita gli amici, non andarci da solo";
    text = summary?.trim() ? `${title} · ${summary} · ${inviteLine}` : `${title} · ${inviteLine}`;
  }

  async function doNativeShare() {
    const payload = { channel: "native", context, placement, url: abs };
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({ title, text, url: links.native });
        trackShare("share_completed", payload);
      } else {
        await navigator.clipboard.writeText(links.copy);
        setCopied(true);
        trackShare("share_click", { ...payload, channel: "copy-fallback" });
        setTimeout(() => setCopied(false), 1600);
      }
    } catch {
      trackShare("share_cancelled", payload);
    }
  }

  function onClickChannel(channel: "facebook" | "copy") {
    trackShare("share_click", { channel, context, placement, url: abs });
  }

  const rootCls = [
    "sharebar",
    compact ? "sharebar--compact" : "",
    align === "center" ? "sharebar--center" : align === "right" ? "sharebar--right" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootCls} role="group" aria-label="Condividi">
      {/* MOBILE ONLY: Native Share - sempre renderizzato per evitare layout shift */}
      <button
        className="sharebtn sharebtn--native sharebtn--mobile-only"
        onClick={doNativeShare}
        aria-label="Condividi"
        data-share-channel="native"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path fill="currentColor" d="M13 5.1V2l7 6-7 6V9a8 8 0 0 0-8 8h-2a10 10 0 0 1 10-10z" />
        </svg>
        <span className="sharebtn-label">Condividi</span>
      </button>

      {/* DESKTOP ONLY: Copy link (primo) */}
      <button
        className="sharebtn sharebtn--desktop-only"
        onClick={async () => {
          await navigator.clipboard.writeText(links.copy);
          setCopied(true);
          onClickChannel("copy");
          setTimeout(() => setCopied(false), 1600);
        }}
        aria-label="Copia link"
        title="Copia link"
        data-share-channel="copy"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            fill="currentColor"
            d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 14H8V7h11v12z"
          />
        </svg>
      </button>

      {/* DESKTOP ONLY: Facebook */}
      <a
        className="sharebtn sharebtn--desktop-only"
        href={links.facebook}
        target="_blank"
        rel="noopener nofollow"
        aria-label="Condividi su Facebook"
        title="Facebook"
        onClick={() => onClickChannel("facebook")}
        data-share-channel="facebook"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            fill="currentColor"
            d="M13 22v-8h3l1-4h-4V7c0-1.1.9-2 2-2h2V1h-3a5 5 0 0 0-5 5v4H6v4h3v8h4z"
          />
        </svg>
      </a>

      {copied && <span className="share-copytip">Copiato</span>}

      <noscript>
        <p style={{ marginTop: 8, fontSize: 12 }}>
          Condividi:{" "}
          <a href={links.facebook} rel="nofollow" className="sharebtn--desktop-only">
            Facebook
          </a>
        </p>
      </noscript>
    </div>
  );
}
